import { memo, useCallback, useEffect, useMemo } from 'react';
import { Currency, Divider, Input, Menu } from '@wg/wows-react-uikit';

import { t } from '~/helpers/localization';

import { currencyNames } from '~/UIKit/Currency/Currency';

import styles from './Calculator.scss';

import type { CurrencyType } from '@wg/wows-react-uikit/components/Currency/Currency';
import type { Delta } from '~/types/declaration';

type BalanceItem = {
  type: CurrencyType;
  amount: number;
  canDistribute?: boolean;
};

interface CalculatorProps {
  distributionSum: number;
  selectedDistributionCurrency: CurrencyType;
  totalBalanceList: Delta[];
  treasuryMinDistributionSumm: number;
  userCount: number;
  selectDistributionCurrency: (type: CurrencyType) => void;
  setDistributionSum: (sum: number) => void;
}

const Calculator = ({
  distributionSum,
  selectedDistributionCurrency,
  totalBalanceList = [],
  userCount,
  selectDistributionCurrency,
  setDistributionSum,
}: CalculatorProps) => {
  const balances = totalBalanceList;

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = Number(e.target.value);

      if (val < 0 || Number.isNaN(val)) {
        val = 0;
      }

      setDistributionSum(val);
    },
    [setDistributionSum],
  );

  const onCurrencySelect = useCallback(
    (value: CurrencyType) => {
      selectDistributionCurrency(value);
    },
    [selectDistributionCurrency],
  );

  const selectedCurrency = useMemo<BalanceItem>(() => {
    const found = balances.find((item) => item.type === selectedDistributionCurrency);
    return found ?? balances[0] ?? ({ type: 'oil' as CurrencyType, amount: 0 } as BalanceItem);
  }, [balances, selectedDistributionCurrency]);

  const isSingle = userCount === 1;

  const totalSum = useMemo(() => Math.round(userCount * distributionSum), [userCount, distributionSum]);

  const total = selectedCurrency.amount;
  const rest = total - totalSum;

  useEffect(() => {
    if (rest < 0 && userCount > 0) {
      setDistributionSum(Math.floor(total / userCount));
    }
  }, [rest, setDistributionSum, total, userCount]);

  const menuItems = useMemo(() => {
    return balances
      .filter((item) => item.canDistribute)
      .map((item) => ({
        value: item.type,
        content: currencyNames[item.type] as string,
      }));
  }, [balances]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.row}>
        <div className={styles.label}>{t('Распределяемый ресурс')}</div>
        <div className={`${styles.resourse} ${styles.menuWrapper}`}>
          <Menu<CurrencyType>
            isSmallPadding={true}
            items={menuItems}
            itemSelected={selectedDistributionCurrency}
            onItemChange={onCurrencySelect}
            dropdownPosition={undefined}
          >
            <div className={styles.menuCurrencyWrapper}>
              <Currency isFlat isBold amount={total} type={selectedCurrency.type} />
            </div>
          </Menu>
        </div>
      </div>

      <Divider type={'minor'} />

      {isSingle ? (
        <div className={styles.row}>
          <div className={styles.label}>{t('Начислить игроку')}</div>
          <div className={styles.resourseInput}>
            <Input
              error={''}
              help={''}
              info={''}
              label={''}
              maxLength={10}
              placeholder={'0'}
              value={`${distributionSum || ''}`}
              width={'64'}
              noimesupport={false}
              isValidating={false}
              isCounted={false}
              isInlined={false}
              isTextCentered={false}
              isFocusOn={false}
              isDisabled={false}
              onBlur={() => {}}
              onChange={onChange}
              onFocus={() => {}}
              onKeyPress={() => {}}
            />
            <Currency isFlat isBold amount={0} type={selectedCurrency.type} onlyIcon />
          </div>
        </div>
      ) : (
        <>
          <div className={styles.row}>
            <div className={styles.label}>{t('Начислить каждому игроку')}</div>
            <div className={styles.resourseInput}>
              <Input
                error={''}
                help={''}
                info={''}
                label={''}
                maxLength={10}
                placeholder={''}
                value={`${distributionSum}`}
                width={'64px'}
                noimesupport={false}
                isValidating={false}
                isCounted={false}
                isInlined={false}
                isTextCentered={false}
                isFocusOn={false}
                isDisabled={false}
                onBlur={() => {}}
                onChange={onChange}
                onFocus={() => {}}
                onKeyPress={() => {}}
              />
              <Currency isFlat isBold amount={0} type={selectedCurrency.type} onlyIcon />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.label}>{t('Будет распределено')}</div>
            <div className={styles.resourse}>
              <Currency isFlat isBold amount={totalSum} type={selectedCurrency.type} />
            </div>
          </div>
        </>
      )}

      <Divider type={'minor'} />

      <div className={styles.row}>
        <div className={styles.label}>{t('Остаток после распределения')}</div>
        <div className={styles.resourse}>
          <Currency isFlat isBold amount={rest} type={selectedCurrency.type} />
        </div>
      </div>
    </div>
  );
};

export default memo(Calculator);
